import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "@Pages/Search/components/FilterSection/data/filtersList";
import { formatAmenityName } from "@Pages/Search/components/FilterSection/components/Filters/AmenitiesFilter/utils/formatAmenityName";
import { formatBillName } from "@Pages/Search/components/FilterSection/components/Filters/BillsIncludedFilter/utils/formatBillName";
import {
  AppliedFilterSequenceItem,
  AppliedFilterSource,
} from "@Pages/Search/utils/appliedFilterSequence";

export type AppliedFilterPill = {
  source: AppliedFilterSource;
  value: string;
  label: string;
  iconKey: string;
  // Card tag texts this pill replaces, as rendered (may be localized). Lets the card drop the
  // duplicate even when the text has no ASCII key (FR-14, O-2).
  replacesTagTexts?: string[];
};

/** English names, also the translation keys ("No Visa No Pay" / "No Place No Pay"). */
const CANCELLATION_POLICY_LABELS: Record<string, string> = {
  no_visa_no_pay: "No Visa No Pay",
  no_place_no_pay: "No Place No Pay",
};

const getCancellationPolicyEntries = (item: any, tag: string): any[] =>
  (Array.isArray(item?.feature_tags) ? item.feature_tags : []).filter(
    (entry: any) => entry?.tag === tag,
  );

/**
 * D-4: a property offers a policy only when its `feature_tags` entry for that tag is flagged
 * `policy_applicable === "true"`. The flag is a string; `"false"` and a missing flag both mean no.
 */
export const offersCancellationPolicy = (item: any, tag: string): boolean =>
  getCancellationPolicyEntries(item, tag).some(
    (entry: any) => String(entry?.policy_applicable) === "true",
  );

const ROOM_TYPE_OPTIONS = [
  ...ROOM_TYPE_SPECIFIC.list,
  ...ROOM_TYPE_FILTER.list,
  ...SHARING_OPTIONS_BATHROOM.list,
  ...SHARING_OPTIONS_KITCHEN.list,
];

export const normalizeFilterTagKey = (value?: string): string =>
  (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const findMatchingTag = (
  value: string,
  uniqueTags: string[] = [],
  uniqueTagsEn: string[] = [],
): { display: string; en: string } | null => {
  const key = normalizeFilterTagKey(value);
  if (!key) return null;
  const lookup = uniqueTagsEn?.length ? uniqueTagsEn : uniqueTags;
  const index = lookup.findIndex((tag) => {
    const tagKey = normalizeFilterTagKey(tag);
    return Boolean(tagKey) && (tagKey === key || tagKey.includes(key) || key.includes(tagKey));
  });
  if (index === -1) return null;
  return {
    display: uniqueTags[index] || lookup[index],
    en: uniqueTagsEn[index] || uniqueTags[index] || lookup[index],
  };
};

const getRoomTypeLabel = (value: string): string => {
  const option = ROOM_TYPE_OPTIONS.find((item) => item.value === value);
  return option?.name || formatAmenityName(value);
};

export const getAppliedFilterPills = ({
  sequence,
  uniqueTags = [],
  uniqueTagsEn = [],
  activeRoomTypes,
  item: cardItem,
}: {
  sequence: AppliedFilterSequenceItem[];
  uniqueTags?: string[];
  uniqueTagsEn?: string[];
  activeRoomTypes?: string[];
  /** The card's inventory; its `feature_tags` gate the cancellation-policy pills. */
  item?: any;
}): AppliedFilterPill[] => {
  const offeredRoomTypes = new Set(
    (activeRoomTypes || []).map((type) => String(type).toLowerCase()),
  );

  return sequence
    .map((item): AppliedFilterPill | null => {
      if (item.source === "roomType") {
        // Exact match only. Composite filters (ensuite, studio, 1b) are stored on
        // child meta.unit_type and returned in active_room_types by the API —
        // do not infer ensuite from private_bathroom (studios also have that).
        if (!offeredRoomTypes.has(item.value.toLowerCase())) return null;
        return {
          source: item.source,
          value: item.value,
          label: getRoomTypeLabel(item.value),
          iconKey: item.value,
        };
      }

      if (item.source === "cancellationPolicy") {
        // Same per-card gate as roomType: a relaxed or recommended card that doesn't offer the
        // policy gets no pill.
        const label = CANCELLATION_POLICY_LABELS[item.value];
        if (!label || !offersCancellationPolicy(cardItem, item.value)) return null;
        return {
          source: item.source,
          value: item.value,
          label,
          iconKey: label,
          replacesTagTexts: getCancellationPolicyEntries(cardItem, item.value)
            .map((entry: any) => entry?.display_name)
            .filter(Boolean),
        };
      }

      const matched = findMatchingTag(item.value, uniqueTags, uniqueTagsEn);
      const formatted =
        item.source === "billsIncluded"
          ? formatBillName(item.value)
          : formatAmenityName(item.value);
      return {
        source: item.source,
        value: item.value,
        label: matched?.display || formatted,
        iconKey: matched?.en || item.value.replace(/_/g, " "),
      };
    })
    .filter((pill): pill is AppliedFilterPill => Boolean(pill));
};

// An empty key is never added: normalizeFilterTagKey strips non-ASCII, so a CJK label becomes ""
// and would hide every CJK tag on the card (O-2).
export const getAppliedFilterExcludeKeys = (pills: AppliedFilterPill[]): Set<string> =>
  new Set(
    pills
      .filter((pill) => pill.source !== "roomType")
      .flatMap((pill) => [normalizeFilterTagKey(pill.label), normalizeFilterTagKey(pill.value)])
      .filter(Boolean),
  );

/** Exact card tag texts replaced by an applied pill (catches localized duplicates). */
export const getAppliedFilterReplacedTexts = (pills: AppliedFilterPill[]): Set<string> =>
  new Set(pills.flatMap((pill) => pill.replacesTagTexts || []));
